import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Bundle picker: one offer (1 / 2 / 3 items) is selected at a time, each item slot picks a
 * character variant, and the whole selection is added to the cart in a single request.
 * Bundle pricing itself is applied at checkout by Shopify automatic discounts.
 */
class TunibuBundles extends HTMLElement {
  connectedCallback() {
    this.offers = [...this.querySelectorAll('[data-offer]')];
    this.submit = this.querySelector('[data-submit]');
    this.submitPrice = this.querySelector('[data-submit-price]');
    this.submitLabel = this.querySelector('[data-submit-label]');
    this.error = this.querySelector('[data-error]');
    this.live = this.querySelector('[data-live]');
    this.defaultLabel = this.submitLabel?.textContent ?? '';
    this.addEventListener('change', this.#onChange);
    this.submit?.addEventListener('click', this.#onSubmit);
    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.submit?.removeEventListener('click', this.#onSubmit);
  }

  get #selectedOffer() {
    return this.offers.find((offer) => offer.querySelector('.tb-offer__radio')?.checked) ?? this.offers[0];
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);


    if (target.classList.contains('tb-swatch__input')) {
      const nameEl = target.closest('[data-slot]')?.querySelector('[data-slot-name]');
      if (nameEl) nameEl.textContent = target.dataset.name ?? '';
    }

    this.#hideError();
    this.#render();
  };

  #render() {
    const selected = this.#selectedOffer;

    for (const offer of this.offers) {
      const isSelected = offer === selected;
      offer.toggleAttribute('data-selected', isSelected);
      const slots = offer.querySelector('[data-slots]');
      if (slots) slots.hidden = !isSelected;
    }

    if (this.submitPrice) this.submitPrice.textContent = selected?.dataset.price ?? '';

    const soldOut = this.#selectedInputs().some((input) => input.dataset.available !== 'true');
    if (this.submit) {
      this.submit.disabled = soldOut;
      this.submit.setAttribute('aria-disabled', String(soldOut));
    }
    if (this.submitLabel) {
      this.submitLabel.textContent = soldOut ? this.dataset.soldOutLabel ?? '' : this.defaultLabel;
    }
  }

  /** @returns {HTMLInputElement[]} */
  #selectedInputs() {
    const offer = this.#selectedOffer;
    if (!offer) return [];
    return [...offer.querySelectorAll('[data-slot]')]
      .map((slot) => /** @type {HTMLInputElement | null} */ (slot.querySelector('.tb-swatch__input:checked')))
      .filter((input) => input !== null);
  }

  /** @returns {Array<{ id: number, quantity: number }>} */
  #lineItems() {
    /** @type {Map<string, number>} */
    const quantities = new Map();
    for (const input of this.#selectedInputs()) {
      quantities.set(input.value, (quantities.get(input.value) ?? 0) + 1);
    }
    return [...quantities].map(([id, quantity]) => ({ id: Number(id), quantity }));
  }

  #onSubmit = async () => {
    if (!this.submit || this.submit.disabled) return;

    const items = this.#lineItems();
    if (items.length === 0) return;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    this.submit.disabled = true;
    this.submit.classList.add('is-loading');

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      const cart = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`).then((res) => res.json());
      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'tunibu-bundles', itemCount: totalQuantity },
        });
        this.#showError(data.description || data.message);
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          sections: data.sections,
          source: 'tunibu-bundles',
          itemCount: totalQuantity,
          productId: this.dataset.productId,
        },
      });
      if (this.live) this.live.textContent = window.Theme?.translations?.added ?? 'Added';
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.#showError('Something went wrong. Please try again.');
    } finally {
      this.submit.classList.remove('is-loading');
      this.#render();
    }
  };

  /** @param {string} message */
  #showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
  }

  #hideError() {
    if (this.error) this.error.hidden = true;
  }
}

if (!customElements.get('tunibu-bundles')) {
  customElements.define('tunibu-bundles', TunibuBundles);
}
